import { useEffect, useState } from "react";
import { Sparkles, Send, CheckCircle2, Circle, Loader2, ArrowRight } from "lucide-react";
import { useBranding } from "@/lib/branding";
import { usePersona } from "@/lib/personaStore";
import { endpoints } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

// Onboarding Copilot — user side.
// Personal Day-1 checklist + short chat grounded on the joiner's own state.

export default function OnboardingCopilot() {
  const { brand } = useBranding();
  const { persona, userName, userEmail } = usePersona();
  const [data, setData] = useState(null);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    endpoints.onboardingUser({ persona: persona.id, user_email: userEmail, user_name: userName })
      .then(setData)
      .catch(() => {});
  }, [persona.id, userEmail, userName]);

  const ask = async () => {
    if (!question.trim()) return;
    setBusy(true);
    try {
      const res = await endpoints.onboardingAsk({
        question,
        persona: persona.id,
        user_email: userEmail,
        user_name: userName,
      });
      setAnswer(res);
      if (res.provider === "canned") toast.info("Copilot returned a canned answer (LLM offline).");
    } catch (e) {
      toast.error("Onboarding copilot unavailable.");
    } finally {
      setBusy(false);
    }
  };

  const brandBlue = brand?.primary || "#0033A0";
  const brandSoft = brand?.primarySoft || "#0033A020";

  if (!data) return null;

  const pct = data.checklist?.length
    ? Math.round((data.checklist.filter((c) => c.done).length / data.checklist.length) * 100)
    : 0;

  return (
    <div
      className="rounded-2xl bg-white p-6 md:p-8"
      style={{ border: `1px solid ${brandSoft}` }}
      data-testid="onboarding-copilot"
    >
      <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
            style={{ backgroundColor: brandSoft, color: brandBlue }}
          >
            <Sparkles size={18} />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] tracking-[0.14em] uppercase font-semibold" style={{ color: brandBlue }}>
              Onboarding Copilot
            </div>
            <div className="text-lg font-semibold text-slate-900 mt-0.5">
              Welcome, {userName?.split(" ")[0] || "there"}.
            </div>
            <div className="text-sm text-slate-500 mt-0.5">{data.summary}</div>
          </div>
        </div>

        {/* Ready % ring */}
        <div className="text-right">
          <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500">Day-1 ready</div>
          <div className="text-3xl font-semibold mt-1" style={{ color: brandBlue }}>{pct}%</div>
        </div>
      </div>

      {/* Checklist */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6">
        {data.checklist?.map((item, i) => (
          <div
            key={i}
            className="flex items-start gap-2 rounded-lg px-3 py-2"
            style={item.done ? { backgroundColor: brandSoft } : { backgroundColor: "#F8FAFC" }}
            data-testid={`onboarding-item-${i}`}
          >
            {item.done ? (
              <CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: brandBlue }} />
            ) : (
              <Circle size={14} className="mt-0.5 shrink-0 text-slate-300" />
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900">{item.label}</div>
              {item.note && <div className="text-[11px] text-slate-500 mt-0.5">{item.note}</div>}
            </div>
          </div>
        ))}
      </div>

      {/* Chat */}
      <div className="pt-6 border-t border-slate-100">
        <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 mb-3">
          Ask the onboarding copilot
        </div>
        <div className="flex gap-2 mb-4">
          <Input
            data-testid="onboarding-input"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !busy) ask(); }}
            placeholder={data.example_q || "What's still missing on my Day-1 kit?"}
            className="h-11 text-[15px]"
          />
          <Button
            data-testid="onboarding-ask-btn"
            onClick={ask}
            disabled={busy || !question.trim()}
            className="h-11 px-5 text-white hover:opacity-90"
            style={{ backgroundColor: brandBlue }}
          >
            {busy ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Send size={14} className="mr-1.5" />}
            Ask
          </Button>
        </div>
        {answer?.answer && (
          <div
            className="rounded-lg p-4"
            style={{ backgroundColor: brandSoft }}
            data-testid="onboarding-answer"
          >
            <p className="text-[15px] leading-7 text-slate-800 whitespace-pre-wrap">{answer.answer}</p>
            <div className="text-[11px] text-slate-500 mt-3">
              {answer.provider === "claude-sonnet-4-5-20250929" ? "Live · Flexspace Copilot" : "Canned fallback"}
            </div>
          </div>
        )}
        {!answer && (
          <div className="flex flex-wrap gap-2">
            {(data.quick_prompts || []).map((p) => (
              <button
                key={p}
                onClick={() => { setQuestion(p); }}
                className="text-xs rounded-full px-3 py-1.5 border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                {p} <ArrowRight size={11} className="inline ml-1" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
